import { type CheerioAPI, load } from 'cheerio';

import type { Heading, Link, LinksGroup } from './interfaces';

/**
 * Class to analyze HTML content for SEO purposes
 * Provides utilities for analyzing links, headings, and text content
 */
export class HtmlAnalyzer {
  private readonly htmlDom: CheerioAPI;
  private readonly siteDomainName: string | null;
  private readonly bodyText: string;

  // URL validation regex
  private static readonly URL_PATTERN = /^(https?:\/\/)/i;
  private static readonly WORD_SPLIT_PATTERN = /\s+/g;

  /**
   * Creates a new HtmlAnalyzer instance
   * @param htmlContent - The HTML content to analyze
   * @param siteDomainName - Optional domain name to determine internal/external links
   */
  constructor(htmlContent: string, siteDomainName: string | null = null) {
    this.htmlDom = load(htmlContent);
    this.siteDomainName = siteDomainName;
    this.bodyText = this.htmlDom.text().toLowerCase();
  }

  /**
   * Extracts all links from the HTML content
   * @returns Array of Link objects containing href and text
   */
  getAllLinks(): Link[] {
    return this.htmlDom('a')
      .map((_, element) => {
        const $el = this.htmlDom(element);
        const href = $el.attr('href');
        return href ? { href, text: $el.text() } : null;
      })
      .get()
      .filter((link): link is Link => link !== null);
  }

  /**
   * Extracts all heading tags (h1-h6) from the HTML content
   * @returns Array of Heading objects containing text and tag name
   */
  getAllHeadingTags(): Heading[] {
    return this.htmlDom('h1,h2,h3,h4,h5,h6')
      .map((_, element) => {
        const $el = this.htmlDom(element);
        return {
          text: $el.text(),
          tag: $el.prop('tagName'),
        };
      })
      .get();
  }

  /**
   * Checks if a URL is relative (starts with ./, ../, /, or #)
   */
  private isRelativeLink(href: string): boolean {
    return /^[./#]/.test(href);
  }

  /**
   * Checks if a URL is absolute (starts with http:// or https://)
   */
  private isAbsoluteLink(href: string): boolean {
    return HtmlAnalyzer.URL_PATTERN.test(href);
  }

  /**
   * Checks if a URL is a mailto link
   */
  private isMailToLink(href: string): boolean {
    return href.startsWith('mailto:');
  }

  /**
   * Determines if a URL is internal to the site
   * A link is considered internal if it's either:
   * - A relative link (not mailto)
   * - An absolute link containing the site's domain
   */
  private isInternalLink(href: string): boolean {
    if (!this.siteDomainName) return false;
    if (href.startsWith('/')) return true;
    if (href.startsWith('#')) return true;
    try {
      const url = new URL(href);
      return url.hostname === this.siteDomainName;
    } catch {
      return false;
    }
  }

  /**
   * Groups links based on a filter function
   * @param links - Array of links to group
   * @param filterFn - Function to determine which links to include
   * @returns Object containing all matching links, unique links, and duplicate links
   */
  private groupLinks(
    links: Link[],
    filterFn: (href: string) => boolean,
  ): LinksGroup {
    const filtered = links.filter((link) => filterFn(link.href));
    const uniqueMap = new Map<string, Link>();
    const duplicates: Link[] = [];

    filtered.forEach((link) => {
      if (uniqueMap.has(link.href)) {
        duplicates.push(link);
      } else {
        uniqueMap.set(link.href, link);
      }
    });

    return {
      all: filtered,
      unique: Array.from(uniqueMap.values()),
      duplicate: duplicates,
    };
  }

  /**
   * Gets all outbound (external) links from the HTML content
   * @returns Grouped links object containing all, unique, and duplicate external links
   */
  getOutboundLinks(): LinksGroup {
    const links = this.getAllLinks();
    return this.groupLinks(links, (href) => !this.isInternalLink(href));
  }

  /**
   * Gets all internal links from the HTML content
   * @returns Grouped links object containing all, unique, and duplicate internal links
   */
  getInternalLinks(): LinksGroup {
    const links = this.getAllLinks();
    return this.groupLinks(links, (href) => this.isInternalLink(href));
  }

  /**
   * Cleans text content by removing extra whitespace
   * @param content - Text content to clean
   * @returns Cleaned text with normalized spacing
   */
  getPureText(content: string): string {
    return content.trim().replace(HtmlAnalyzer.WORD_SPLIT_PATTERN, ' ');
  }

  /**
   * Counts words in the provided text or the entire HTML body
   * @param content - Optional text to count words in. If not provided, uses entire body text
   * @returns Number of words in the text
   */
  getWordCount(content?: string | null): number {
    const textToAnalyze = content?.toLowerCase() ?? this.bodyText;
    return this.getPureText(textToAnalyze).split(' ').length;
  }

  /**
   * Gets the cleaned text content
   * @returns Cleaned text content with normalized spacing
   */
  getTextContent(): string {
    return this.getPureText(this.bodyText);
  }
}
